import { useEffect, useState } from 'react';
import {
  loadHubSpotMeasureOptions,
  loadMeasurableLists,
  type HubSpotMeasureOptions,
  type MeasurableList,
} from '@/lib/objective-progress';
import type {
  HubSpotMeetingOutcome,
  WorkspaceObjectiveMeasure,
  WorkspaceObjectiveMeasureInput,
  WorkspaceObjectiveMeasureSource,
} from '@shared/types/workspace-objectives';

export interface MeasureDraft {
  source: WorkspaceObjectiveMeasureSource | '';
  collectionId: string;
  statusKey: string;
  portalId: string;
  pipelineId: string;
  pipelineLabel: string;
  stageId: string;
  stageLabel: string;
  meetingType: string;
  meetingTypeLabel: string;
  meetingOutcome: HubSpotMeetingOutcome | '';
  target: string;
  startsOn: string;
}

export const MEETING_OUTCOMES: Array<{ value: HubSpotMeetingOutcome; label: string }> = [
  { value: 'SCHEDULED', label: 'Planifiée' },
  { value: 'COMPLETED', label: 'Terminée' },
  { value: 'RESCHEDULED', label: 'Replanifiée' },
  { value: 'NO_SHOW', label: 'Absence' },
  { value: 'CANCELED', label: 'Annulée' },
];

const EMPTY_SOURCE_FIELDS = {
  collectionId: '', statusKey: '', portalId: '', pipelineId: '', pipelineLabel: '', stageId: '', stageLabel: '',
  meetingType: '', meetingTypeLabel: '', meetingOutcome: '' as const,
};

export const measureDraftFrom = (
  measure: WorkspaceObjectiveMeasure | undefined,
  defaultStart: string,
): MeasureDraft => measure
  ? {
      source: measure.source,
      collectionId: measure.collection_id ?? '',
      statusKey: measure.status_key ?? '',
      portalId: measure.hubspot_portal_id ?? '',
      pipelineId: measure.hubspot_pipeline_id ?? '',
      pipelineLabel: measure.hubspot_pipeline_label ?? '',
      stageId: measure.hubspot_stage_id ?? '',
      stageLabel: measure.hubspot_stage_label ?? '',
      meetingType: measure.hubspot_meeting_type ?? '',
      meetingTypeLabel: measure.hubspot_meeting_type_label ?? '',
      meetingOutcome: measure.hubspot_meeting_outcome ?? '',
      target: String(measure.target_value),
      startsOn: measure.starts_on,
    }
  : { source: '', ...EMPTY_SOURCE_FIELDS, target: '', startsOn: defaultStart };

const INCOMPLETE = 'Pour mesurer l’objectif, choisissez ce qui compte, une cible entière et une date de début.';

/** Null when no source is chosen (no measure); an error message when incomplete. */
export const toMeasureInput = (draft: MeasureDraft): WorkspaceObjectiveMeasureInput | null | string => {
  if (!draft.source) return null;
  const target = Number(draft.target);
  if (!Number.isInteger(target) || target < 1 || !draft.startsOn) return INCOMPLETE;
  const base: WorkspaceObjectiveMeasureInput = {
    source: draft.source,
    collection_id: null,
    status_key: null,
    hubspot_portal_id: null,
    hubspot_pipeline_id: null,
    hubspot_pipeline_label: null,
    hubspot_stage_id: null,
    hubspot_stage_label: null,
    hubspot_meeting_type: null,
    hubspot_meeting_type_label: null,
    hubspot_meeting_outcome: null,
    target_value: target,
    starts_on: draft.startsOn,
  };
  if (draft.source === 'app_status') {
    if (!draft.collectionId || !draft.statusKey) return INCOMPLETE;
    return { ...base, collection_id: draft.collectionId, status_key: draft.statusKey };
  }
  if (!draft.portalId) return 'HubSpot n’est pas connecté pour l’entreprise.';
  if (draft.source === 'hubspot_deal_stage') {
    if (!draft.pipelineId || !draft.stageId) return INCOMPLETE;
    return {
      ...base,
      hubspot_portal_id: draft.portalId,
      hubspot_pipeline_id: draft.pipelineId,
      hubspot_pipeline_label: draft.pipelineLabel,
      hubspot_stage_id: draft.stageId,
      hubspot_stage_label: draft.stageLabel,
    };
  }
  if (!draft.meetingType && !draft.meetingOutcome) {
    return 'Pour compter des réunions, choisissez un type ou un résultat.';
  }
  return {
    ...base,
    hubspot_portal_id: draft.portalId,
    hubspot_meeting_type: draft.meetingType || null,
    hubspot_meeting_type_label: draft.meetingType ? draft.meetingTypeLabel : null,
    hubspot_meeting_outcome: draft.meetingOutcome || null,
  };
};

const useLoaded = <T,>(enabled: boolean, load: () => Promise<T>) => {
  const [value, setValue] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!enabled || value !== null) return;
    let active = true;
    void load()
      .then((loaded) => { if (active) setValue(loaded); })
      .catch((cause: unknown) => {
        if (active) setError(cause instanceof Error ? cause.message : 'Source inaccessible.');
      });
    return () => { active = false; };
  }, [enabled, value, load]);
  return { value, error };
};

/** Optional measure of an objective, from an exact source chosen per client:
 * an app list status, a HubSpot deal stage or HubSpot meetings. Counting rules
 * live in docs/DATA_MODEL.md, not on screen. */
export const ObjectiveMeasureFields = ({ draft, onChange, fieldClass }: {
  draft: MeasureDraft;
  onChange: (draft: MeasureDraft) => void;
  fieldClass: string;
}) => {
  const isHubSpot = draft.source === 'hubspot_deal_stage' || draft.source === 'hubspot_meeting';
  const lists = useLoaded<MeasurableList[]>(draft.source === 'app_status', loadMeasurableLists);
  const hubspot = useLoaded<HubSpotMeasureOptions>(isHubSpot, loadHubSpotMeasureOptions);
  const list = lists.value?.find((candidate) => candidate.id === draft.collectionId) ?? null;
  const pipeline = hubspot.value?.pipelines.find((candidate) => candidate.id === draft.pipelineId) ?? null;

  // The portal of a new HubSpot measure is the one the tenant reads now.
  useEffect(() => {
    if (isHubSpot && hubspot.value?.portalId && !draft.portalId) {
      onChange({ ...draft, portalId: hubspot.value.portalId });
    }
  }, [isHubSpot, hubspot.value, draft, onChange]);

  const sourceError = draft.source === 'app_status' ? lists.error : isHubSpot ? hubspot.error : null;
  const loading = (draft.source === 'app_status' && lists.value === null && !lists.error) ||
    (isHubSpot && hubspot.value === null && !hubspot.error);
  const hubspotMissing = isHubSpot && hubspot.value?.available === false;

  return (
    <fieldset className='rounded-xl border border-domino-cardBorder px-4 pb-4 pt-2'>
      <legend className='px-1 text-sm font-medium text-domino-ink'>Mesure</legend>
      <div className='grid gap-4 sm:grid-cols-2'>
        <label className='text-sm font-medium text-domino-ink sm:col-span-2'>
          Compter<select
            value={draft.source}
            onChange={(event) => onChange({
              ...draft,
              ...EMPTY_SOURCE_FIELDS,
              source: event.target.value as MeasureDraft['source'],
            })}
            className={fieldClass}
          >
            <option value=''>Pas de mesure</option>
            <option value='app_status'>Lignes d’une liste d’application</option>
            <option value='hubspot_deal_stage'>Affaires HubSpot entrées dans une étape</option>
            <option value='hubspot_meeting'>Réunions HubSpot</option>
          </select>
        </label>
        {sourceError
          ? <p role='alert' className='text-sm text-red-700 sm:col-span-2'>{sourceError}</p>
          : loading
          ? <p role='status' className='text-sm text-domino-inkSoft sm:col-span-2'>Chargement…</p>
          : hubspotMissing
          ? <p className='text-sm text-domino-inkSoft sm:col-span-2'>HubSpot n’est pas connecté pour l’entreprise.</p>
          : draft.source === 'app_status' && lists.value?.length === 0 && !draft.collectionId
          ? <p className='text-sm text-domino-inkSoft sm:col-span-2'>Aucune liste d’application à compter.</p>
          : draft.source && (
            <>
              {draft.source === 'app_status' && (
                <>
                  <label className='text-sm font-medium text-domino-ink'>
                    Liste<select
                      value={draft.collectionId}
                      onChange={(event) => onChange({ ...draft, collectionId: event.target.value, statusKey: '' })}
                      className={fieldClass}
                    >
                      <option value='' disabled>Choisir une liste</option>
                      {lists.value?.map((candidate) => <option key={candidate.id} value={candidate.id}>{candidate.title}</option>)}
                    </select>
                  </label>
                  <label className='text-sm font-medium text-domino-ink'>
                    Passées au statut<select
                      value={draft.statusKey}
                      onChange={(event) => onChange({ ...draft, statusKey: event.target.value })}
                      className={fieldClass}
                    >
                      <option value='' disabled>Choisir un statut</option>
                      {!list && draft.statusKey && <option value={draft.statusKey}>{draft.statusKey}</option>}
                      {list?.statuses.map((status) => <option key={status.key} value={status.key}>{status.label}</option>)}
                    </select>
                  </label>
                </>
              )}
              {draft.source === 'hubspot_deal_stage' && (
                <>
                  <label className='text-sm font-medium text-domino-ink'>
                    Pipeline<select
                      value={draft.pipelineId}
                      onChange={(event) => {
                        const chosen = hubspot.value?.pipelines.find((candidate) => candidate.id === event.target.value);
                        onChange({ ...draft, pipelineId: event.target.value, pipelineLabel: chosen?.label ?? '', stageId: '', stageLabel: '' });
                      }}
                      className={fieldClass}
                    >
                      <option value='' disabled>Choisir un pipeline</option>
                      {!pipeline && draft.pipelineId && <option value={draft.pipelineId}>{draft.pipelineLabel}</option>}
                      {hubspot.value?.pipelines.map((candidate) => <option key={candidate.id} value={candidate.id}>{candidate.label}</option>)}
                    </select>
                  </label>
                  <label className='text-sm font-medium text-domino-ink'>
                    Entrées dans l’étape<select
                      value={draft.stageId}
                      onChange={(event) => {
                        const chosen = pipeline?.stages.find((stage) => stage.id === event.target.value);
                        onChange({ ...draft, stageId: event.target.value, stageLabel: chosen?.label ?? '' });
                      }}
                      className={fieldClass}
                    >
                      <option value='' disabled>Choisir une étape</option>
                      {draft.stageId && !pipeline?.stages.some((stage) => stage.id === draft.stageId) && (
                        <option value={draft.stageId}>{draft.stageLabel}</option>
                      )}
                      {pipeline?.stages.map((stage) => <option key={stage.id} value={stage.id}>{stage.label}</option>)}
                    </select>
                  </label>
                </>
              )}
              {draft.source === 'hubspot_meeting' && (
                <>
                  <label className='text-sm font-medium text-domino-ink'>
                    Type<select
                      value={draft.meetingType}
                      onChange={(event) => {
                        const chosen = hubspot.value?.meetingTypes.find((type) => type.value === event.target.value);
                        onChange({ ...draft, meetingType: event.target.value, meetingTypeLabel: chosen?.label ?? '' });
                      }}
                      className={fieldClass}
                    >
                      <option value=''>Tous les types</option>
                      {draft.meetingType && !hubspot.value?.meetingTypes.some((type) => type.value === draft.meetingType) && (
                        <option value={draft.meetingType}>{draft.meetingTypeLabel}</option>
                      )}
                      {hubspot.value?.meetingTypes.map((type) => <option key={type.value} value={type.value}>{type.label}</option>)}
                    </select>
                  </label>
                  <label className='text-sm font-medium text-domino-ink'>
                    Résultat<select
                      value={draft.meetingOutcome}
                      onChange={(event) => onChange({ ...draft, meetingOutcome: event.target.value as MeasureDraft['meetingOutcome'] })}
                      className={fieldClass}
                    >
                      <option value=''>Tous les résultats</option>
                      {MEETING_OUTCOMES.map((outcome) => <option key={outcome.value} value={outcome.value}>{outcome.label}</option>)}
                    </select>
                  </label>
                </>
              )}
              <label className='text-sm font-medium text-domino-ink'>
                Cible<input
                  type='number'
                  min={1}
                  step={1}
                  inputMode='numeric'
                  value={draft.target}
                  onChange={(event) => onChange({ ...draft, target: event.target.value })}
                  className={fieldClass}
                />
              </label>
              <label className='text-sm font-medium text-domino-ink'>
                À partir du<input
                  type='date'
                  value={draft.startsOn}
                  onChange={(event) => onChange({ ...draft, startsOn: event.target.value })}
                  className={fieldClass}
                />
              </label>
            </>
          )}
      </div>
    </fieldset>
  );
};
